import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Build as InstallationIcon,
  Edit,
  Save,
  Close,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { InstallationService, InstallationServiceImportResult } from '../../types';
import { CatalogListToolbar } from '../common/CatalogListToolbar';
import {
  SortableFilterableHeader,
  FreeSoloAutocompleteField,
  getDistinctValues,
} from '../common/SortableFilterableHeader';

// Worker brief "catalog-list-ui-pattern" (2026-09-05): mismo tratamiento que
// ya recibió ProductList.tsx en la ronda anterior - CatalogListToolbar +
// SortableFilterableHeader reemplazan la barra/filtros propios que vivía
// esta pantalla. `unidad` y `complejidad` son los dos campos genuinamente
// de texto libre/categórico (los otros dos - codigo, descripcion, precio -
// no lo son); `estado` es un tercer filtro derivado de `is_active` (no hay
// un campo de texto "estado" en InstallationService, solo el booleano).
// `unidad` dropped from sort (worker brief "catalog-list-filter-polish",
// 2026-09-06) - it keeps its column filter below, just not sortable.
type SortableColumn = 'complejidad';
type FilterableColumn = 'unidad' | 'complejidad' | 'estado';

const ESTADO_ACTIVO = 'Activo';
const ESTADO_INACTIVO = 'Inactivo';

const getEstadoLabel = (installation: InstallationService): string =>
  installation.is_active ? ESTADO_ACTIVO : ESTADO_INACTIVO;

export const InstallationList: React.FC = () => {
  const { user } = useAuth();

  // Estados
  const [installations, setInstallations] = useState<InstallationService[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Ordenamiento y filtro por columna (worker brief "catalog-list-ui-pattern")
  // - reemplaza el Select de Estado que vivía arriba de la tabla; ahora vive
  // en cada encabezado vía SortableFilterableHeader, igual que ProductList.tsx.
  const [sortColumn, setSortColumn] = useState<SortableColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  // Multi-select, OR-combined per column (worker brief
  // "catalog-list-filter-polish", 2026-09-06) - empty array = unfiltered.
  const [columnFilters, setColumnFilters] = useState<Record<FilterableColumn, string[]>>({
    unidad: [],
    complejidad: [],
    estado: [],
  });

  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50); // 50 servicios por página por defecto

  // Estados para importación de archivo CSV/XLS (CLAUDE.md §7, decidido
  // 2026-09-02 - reemplaza la sincronización en vivo con Google Sheets).
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<InstallationServiceImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edición inline (pedido directo del usuario, ver worker brief
  // "admin-inline-edit"): lápiz junto al código -> fila editable ->
  // Guardar/Cancelar, igual patrón que ProductList.tsx. Solo una fila
  // editable a la vez. `unidad`/`complejidad` ahora usan
  // FreeSoloAutocompleteField (worker brief "catalog-list-ui-pattern");
  // `estado`/is_active sigue con su propio Select dedicado, sin tocar.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    codigo: '', descripcion: '', unidad: '', complejidad: '', precio: '', currency: 'ARS', is_active: true,
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Distintos valores por columna (worker brief "catalog-list-ui-pattern"):
  // computados client-side sobre el dataset ya cargado (`installations`, que
  // ahora siempre trae todos los estados - ver loadInstallations) - alimenta
  // tanto el dropdown de filtro del encabezado como el Autocomplete
  // freeSolo de edición inline de la misma columna.
  const unidadOptions = getDistinctValues(installations, 'unidad');
  const complejidadOptions = getDistinctValues(installations, 'complejidad');
  const estadoOptions = Array.from(new Set(installations.map(getEstadoLabel))).sort();

  const handleColumnFilterChange = (column: FilterableColumn, values: string[]) => {
    setColumnFilters(prev => ({ ...prev, [column]: values }));
    setCurrentPage(1);
  };

  const handleSort = (column: SortableColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  // Búsqueda por código/descripción/unidad + filtros de columna, 100%
  // client-side sobre el dataset ya cargado (comportamiento preexistente de
  // esta pantalla - a diferencia de ProductList.tsx, la búsqueda acá nunca
  // disparó un round-trip al backend).
  const filteredInstallations = installations.filter((installation) => {
    const matchesSearch = !debouncedSearchTerm ||
      installation.codigo.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      (installation.descripcion && installation.descripcion.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
      (installation.unidad && installation.unidad.toLowerCase().includes(debouncedSearchTerm.toLowerCase()));
    if (!matchesSearch) return false;
    if (columnFilters.unidad.length > 0 && !columnFilters.unidad.includes(installation.unidad || '')) return false;
    if (columnFilters.complejidad.length > 0 && !columnFilters.complejidad.includes(installation.complejidad || '')) return false;
    if (columnFilters.estado.length > 0 && !columnFilters.estado.includes(getEstadoLabel(installation))) return false;
    return true;
  });

  const sortedInstallations = React.useMemo(() => {
    if (!sortColumn) return filteredInstallations;
    const withCollator = [...filteredInstallations].sort((a, b) => {
      const aValue = (a[sortColumn] as string) || '';
      const bValue = (b[sortColumn] as string) || '';
      return aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' });
    });
    return sortDirection === 'asc' ? withCollator : withCollator.reverse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredInstallations, sortColumn, sortDirection]);

  // Cálculos de paginación
  const totalInstallations = sortedInstallations.length;
  const totalPages = Math.ceil(totalInstallations / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInstallations = sortedInstallations.slice(startIndex, endIndex);

  // Reset página cuando cambia la búsqueda, filtros, o el tamaño de página
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, itemsPerPage]);

  // Cargar instalaciones - worker brief "catalog-list-ui-pattern": ya no se
  // envía `is_active` al backend, se traen todos los estados de entrada
  // (mismo cambio que ProductList.tsx hizo con `all_statuses: true`) para
  // que el filtro de Estado del encabezado tenga ambos valores disponibles
  // sin depender de un segundo round-trip.
  const loadInstallations = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.get<InstallationService[]>('/installation-services/', {
        params: { limit: 1000 },
      });

      setInstallations(response || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar instalaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInstallations(); // Initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handlers
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset página al buscar
  };

  const handleLimitChange = (event: any) => {
    setItemsPerPage(event.target.value as number);
    setCurrentPage(1);
  };

  const handleOpenImportPicker = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (file: File) => {
    try {
      setImporting(true);
      setImportError(null);
      setImportResult(null);

      const formData = new FormData();
      formData.append('file', file);
      const result = await apiService.post<InstallationServiceImportResult>(
        '/installation-services/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setImportResult(result);

      // Recargar instalaciones
      await loadInstallations();
    } catch (err: any) {
      console.error('Error importing installation services file:', err);
      setImportError(err.response?.data?.detail || 'Error al importar el archivo');
    } finally {
      setImporting(false);
    }
  };

  // Worker brief "catalog-list-ui-pattern": plantilla CSV de arranque para
  // el importador de arriba, mismo patrón que ProductList.tsx (descarga vía
  // Blob, no un <a href> directo - la autenticación es un header Bearer
  // sobre la instancia de axios, no una cookie).
  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.installationServices.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'plantilla_mano_de_obra.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading template:', err);
      setError('Error al descargar la plantilla');
    }
  };

  const handleStartEdit = (installation: InstallationService) => {
    setEditingId(installation.id);
    setEditError(null);
    setEditForm({
      codigo: installation.codigo ?? '',
      descripcion: installation.descripcion ?? '',
      unidad: installation.unidad ?? '',
      complejidad: installation.complejidad ?? '',
      precio: installation.precio != null ? String(installation.precio) : '',
      currency: installation.currency || 'ARS',
      is_active: installation.is_active,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditError(null);
  };

  const handleEditFieldChange = (field: keyof typeof editForm, value: string | boolean) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async (installation: InstallationService) => {
    if (!editForm.codigo.trim() || !editForm.descripcion.trim()) {
      setEditError('Código y descripción no pueden estar vacíos');
      return;
    }
    if (editForm.precio.trim() === '' || Number(editForm.precio) < 0) {
      setEditError('El precio debe ser un número mayor o igual a 0');
      return;
    }
    try {
      setSavingEdit(true);
      setEditError(null);
      const updated = await apiService.installationServices.update(installation.id, {
        codigo: editForm.codigo.trim(),
        descripcion: editForm.descripcion.trim(),
        unidad: editForm.unidad.trim() || undefined,
        complejidad: editForm.complejidad.trim() || undefined,
        precio: Number(editForm.precio),
        currency: editForm.currency,
        is_active: editForm.is_active,
      });
      setInstallations(prev => prev.map(i => (i.id === installation.id ? updated : i)));
      setEditingId(null);
    } catch (err: any) {
      console.error('Error saving installation service inline edit:', err);
      setEditError(err.response?.data?.detail || 'Error al guardar los cambios');
    } finally {
      setSavingEdit(false);
    }
  };

  // Currency restaurado (CLAUDE.md §8.5, 2026-09-01) - cada fila puede estar
  // en USD o ARS, ya no siempre USD. PRECIO = 0 significa "Consultar"
  // (precio a confirmar, CLAUDE.md §7), no un precio real gratuito.
  const formatPrice = (price?: number, currency?: string) => {
    if (price == null) return '-';
    if (price === 0) return 'Consultar';
    const symbol = currency === 'ARS' ? 'AR$' : 'U$';
    return `${symbol} ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const columnCount = user?.role === 'admin' ? 7 : 6;

  if (loading && installations.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <CatalogListToolbar
        title="Mano de Obra"
        itemNoun="Mano de Obra"
        isAdmin={user?.role === 'admin'}
        onImportClick={handleOpenImportPicker}
        onDownloadTemplate={handleDownloadTemplate}
        searchValue={searchTerm}
        onSearchChange={handleSearchChange}
        searchPlaceholder="Buscar por código o descripción..."
        subtitle={
          <>
            {totalInstallations > 0 ? (
              <>
                Mostrando {startIndex + 1}-{Math.min(endIndex, totalInstallations)} de {totalInstallations} servicio{totalInstallations !== 1 ? 's' : ''}
                {totalPages > 1 && ` (Página ${currentPage} de ${totalPages})`}
              </>
            ) : (
              'No hay servicios de instalación para mostrar'
            )}
            {searchTerm && ` (filtrado por: "${searchTerm}")`}
          </>
        }
        extraControls={
          <FormControl sx={{ minWidth: '120px' }} size="small">
            <InputLabel>Por página</InputLabel>
            <Select
              value={itemsPerPage}
              label="Por página"
              onChange={handleLimitChange}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
              <MenuItem value={500}>Todos</MenuItem>
            </Select>
          </FormControl>
        }
      />

      {/* Input de archivo oculto - el botón "Importar" vive en CatalogListToolbar,
          este input solo se dispara programáticamente (handleOpenImportPicker). */}
      <input
        type="file"
        ref={fileInputRef}
        hidden
        accept=".xlsx,.xls,.csv"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImportFile(file);
          e.target.value = '';
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {importError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setImportError(null)}>
          {importError}
        </Alert>
      )}

      {importResult && (
        <Alert
          severity={importResult.errors.length > 0 ? 'warning' : 'success'}
          sx={{ mb: 3 }}
          onClose={() => setImportResult(null)}
        >
          <Typography variant="body2">
            Importación: {importResult.created} creados, {importResult.updated} actualizados
            {importResult.errors.length > 0 && `, ${importResult.errors.length} con error`}.
          </Typography>
          {importResult.errors.slice(0, 10).map((e, i) => (
            <Typography key={i} variant="caption" color="text.secondary" display="block">{e}</Typography>
          ))}
        </Alert>
      )}

      {editError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setEditError(null)}>
          {editError}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 24px 0 rgba(60,72,100,0.10)', overflowX: 'auto' }}>
        <Table>
          <TableHead sx={{ backgroundColor: 'rgb(66,154,45)' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>CODIGO</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>DESCRIPCIÓN</TableCell>
              <SortableFilterableHeader
                label="UNIDAD"
                filterOptions={unidadOptions}
                filterValue={columnFilters.unidad}
                onFilterChange={(v) => handleColumnFilterChange('unidad', v)}
              />
              <SortableFilterableHeader
                label="COMPLEJIDAD"
                onSort={() => handleSort('complejidad')}
                sortActive={sortColumn === 'complejidad'}
                sortDirection={sortDirection}
                filterOptions={complejidadOptions}
                filterValue={columnFilters.complejidad}
                onFilterChange={(v) => handleColumnFilterChange('complejidad', v)}
              />
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>PRECIO</TableCell>
              <SortableFilterableHeader
                label="ESTADO"
                filterOptions={estadoOptions}
                filterValue={columnFilters.estado}
                onFilterChange={(v) => handleColumnFilterChange('estado', v)}
              />
              {user?.role === 'admin' && (
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedInstallations.length > 0 ? paginatedInstallations.map((installation, index) => (
              <TableRow
                key={installation.id}
                sx={{
                  backgroundColor: index % 2 === 0 ? 'white' : '#f5f5f5',
                  transition: 'background 0.2s',
                  '&:hover': {
                    backgroundColor: '#e0f2fe' // celeste muy claro
                  }
                }}
              >
                {editingId === installation.id ? (
                  <>
                    <TableCell>
                      <TextField size="small" variant="standard" value={editForm.codigo} required
                        error={!editForm.codigo.trim()}
                        onChange={(e) => handleEditFieldChange('codigo', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" variant="standard" value={editForm.descripcion} required
                        error={!editForm.descripcion.trim()} sx={{ minWidth: 200 }}
                        onChange={(e) => handleEditFieldChange('descripcion', e.target.value)} />
                    </TableCell>
                    <TableCell align="center">
                      <FreeSoloAutocompleteField
                        value={editForm.unidad}
                        options={unidadOptions}
                        onChange={(v) => handleEditFieldChange('unidad', v)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <FreeSoloAutocompleteField
                        value={editForm.complejidad}
                        options={complejidadOptions}
                        onChange={(v) => handleEditFieldChange('complejidad', v)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1} alignItems="center" justifyContent="center">
                        <TextField size="small" variant="standard" type="number" value={editForm.precio}
                          inputProps={{ step: 0.01, min: 0 }} sx={{ width: 90 }}
                          onChange={(e) => handleEditFieldChange('precio', e.target.value)} />
                        <Select size="small" variant="standard" value={editForm.currency}
                          onChange={(e) => handleEditFieldChange('currency', e.target.value as string)}>
                          <MenuItem value="ARS">ARS</MenuItem>
                          <MenuItem value="USD">USD</MenuItem>
                        </Select>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Select size="small" variant="standard" value={editForm.is_active ? 'true' : 'false'}
                        onChange={(e) => handleEditFieldChange('is_active', e.target.value === 'true')}>
                        <MenuItem value="true">Activo</MenuItem>
                        <MenuItem value="false">Inactivo</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <IconButton size="small" color="success" title="Guardar cambios"
                          disabled={savingEdit || !editForm.codigo.trim() || !editForm.descripcion.trim()}
                          onClick={() => handleSaveEdit(installation)}>
                          <Save />
                        </IconButton>
                        <IconButton size="small" title="Cancelar edición" disabled={savingEdit}
                          onClick={handleCancelEdit}>
                          <Close />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {installation.codigo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {installation.descripcion}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{installation.unidad || '-'}</TableCell>
                    <TableCell align="center">{installation.complejidad || '-'}</TableCell>
                    <TableCell align="center">
                      {formatPrice(installation.precio, installation.currency)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={installation.is_active ? 'Activo' : 'Inactivo'}
                        color={installation.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    {user?.role === 'admin' && (
                      <TableCell align="center">
                        <IconButton size="small" color="primary" title="Editar servicio"
                          onClick={() => handleStartEdit(installation)}>
                          <Edit />
                        </IconButton>
                      </TableCell>
                    )}
                  </>
                )}
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={columnCount} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No hay servicios de instalación para mostrar
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {installations.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <InstallationIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No hay instalaciones
          </Typography>
          <Typography variant="body2" color="textSecondary">
            No hay servicios de instalación disponibles.
          </Typography>
        </Box>
      )}

      {/* Controles de paginación */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3} gap={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, page) => setCurrentPage(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
          <Typography variant="body2" color="text.secondary">
            {startIndex + 1}-{Math.min(endIndex, totalInstallations)} de {totalInstallations} instalaciones (Página {currentPage} de {totalPages})
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default InstallationList;
